import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import useInvalidate from '../hooks/useInvalidate';

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'oficina', label: 'Oficina' },
  { value: 'bodega', label: 'Bodega' },
  { value: 'vendedor', label: 'Vendedor' },
];

const ROL_HINT = {
  admin: '',
  oficina: '',
  bodega: '',
  vendedor: '',
};

export default function UsuariosPage({ pin, onToast }) {
  const [usuarios, setUsuarios] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [filtroRol, setFiltroRol] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [nombre, setNombre] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [bodegaPin, setBodegaPin] = useState('');
  const [rol, setRol] = useState('bodega');
  const [vendedorId, setVendedorId] = useState('');
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  function load() {
    api
      .getUsuarios()
      .then((r) => setUsuarios(r.data || []))
      .catch((err) => {
        setUsuarios([]);
        onToast?.(err.message || 'No se pudieron cargar usuarios', 'error');
      });
    api
      .getVendedores()
      .then((r) => setVendedores(r.data || []))
      .catch((err) => {
        setVendedores([]);
        onToast?.(err.message || 'No se pudieron cargar vendedores', 'error');
      });
  }

  useEffect(() => {
    load();
  }, [pin]);

  useInvalidate(['user', 'seller'], load);

  const vendedorNombre = useMemo(() => {
    const map = new Map(vendedores.map((v) => [Number(v.id), v.nombre]));
    return (id) => (id ? map.get(Number(id)) || `Vendedor #${id}` : null);
  }, [vendedores]);

  const filtrados = usuarios.filter((u) => {
    if (filtroRol && u.rol !== filtroRol) return false;
    if (!mostrarInactivos && !u.activo) return false;
    return true;
  });

  async function guardar(e) {
    e.preventDefault();
    if (!nombre.trim() || !username.trim()) return;
    if (!editId && !password.trim()) return;
    if (password.trim() && password.trim().length < 8) {
      onToast?.('La contraseña debe tener al menos 8 caracteres', 'error');
      return;
    }
    if (rol === 'vendedor' && !vendedorId) {
      onToast?.('Elige el vendedor del catálogo para este login', 'error');
      return;
    }
    if (rol === 'bodega') {
      const pinVal = bodegaPin.trim();
      if (!editId && !pinVal) {
        onToast?.('Asigna el PIN compartido de Bodega (4–8 dígitos)', 'error');
        return;
      }
      if (pinVal && !/^\d{4,8}$/.test(pinVal)) {
        onToast?.('El PIN de Bodega debe tener entre 4 y 8 dígitos', 'error');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        nombre: nombre.trim(),
        username: username.trim(),
        rol,
        vendedor_id: rol === 'vendedor' ? Number(vendedorId) : null,
      };
      if (password.trim()) payload.password = password.trim();
      if (rol === 'bodega' && bodegaPin.trim()) payload.pin = bodegaPin.trim();
      if (editId) {
        await api.actualizarUsuario(editId, payload);
        onToast?.(
          rol === 'bodega' && bodegaPin.trim()
            ? 'Usuario actualizado · PIN compartido guardado'
            : 'Usuario actualizado'
        );
      } else {
        await api.crearUsuario(payload);
        onToast?.('Usuario creado');
      }
      resetForm();
      load();
    } catch (err) {
      onToast?.(err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  function resetForm() {
    setNombre('');
    setUsername('');
    setPassword('');
    setBodegaPin('');
    setRol('bodega');
    setVendedorId('');
    setEditId(null);
  }

  async function desactivar(id) {
    if (!window.confirm('¿Desactivar este usuario? No podrá entrar a la app.')) return;
    try {
      await api.eliminarUsuario(id);
      onToast?.('Usuario desactivado');
      load();
    } catch (err) {
      onToast?.(err.message, 'error');
    }
  }

  async function reactivar(u) {
    try {
      await api.actualizarUsuario(u.id, { activo: 1 });
      onToast?.('Usuario reactivado');
      load();
    } catch (err) {
      onToast?.(err.message, 'error');
    }
  }

  function editar(u) {
    setEditId(u.id);
    setNombre(u.nombre);
    setUsername(u.username || '');
    setPassword('');
    setBodegaPin('');
    setRol(u.rol);
    setVendedorId(u.vendedor_id ? String(u.vendedor_id) : '');
  }

  return (
    <div className="panel">
      <header className="panel-head">
        <div>
          <h2>Usuarios y accesos</h2>
        </div>
      </header>

      <form className="form-row usuarios-form" onSubmit={guardar}>
        <input
          placeholder="Nombre *"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          required
        />
        <input
          placeholder="Usuario *"
          value={username}
          onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))}
          required
        />
        <input
          type="password"
          placeholder={editId ? 'Contraseña (dejar vacío = no cambiar)' : 'Contraseña * (mín. 8)'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={editId ? undefined : 8}
          required={!editId}
        />
        {rol === 'bodega' ? (
          <input
            type="password"
            inputMode="numeric"
            placeholder={editId ? 'PIN compartido (vacío = no cambiar)' : 'PIN compartido * (4–8 dígitos)'}
            value={bodegaPin}
            onChange={(e) => setBodegaPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
            maxLength={8}
            required={!editId}
          />
        ) : null}
        <select value={rol} onChange={(e) => setRol(e.target.value)}>
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        {rol === 'vendedor' ? (
          <select value={vendedorId} onChange={(e) => setVendedorId(e.target.value)} required>
            <option value="">Vincular a vendedor *</option>
            {vendedores.map((v) => (
              <option key={v.id} value={v.id}>{v.nombre}</option>
            ))}
          </select>
        ) : null}
        <button type="submit" disabled={saving}>{editId ? 'Actualizar' : 'Agregar'}</button>
        {editId ? (
          <button type="button" className="ghost" onClick={resetForm}>Cancelar</button>
        ) : null}
      </form>
      {ROL_HINT[rol] ? <p className="muted form-hint">{ROL_HINT[rol]}</p> : null}

      <div className="usuarios-filters">
        <div className="pedidos-admin-chips">
          <button type="button" className={`chip-btn ${!filtroRol ? 'active' : ''}`} onClick={() => setFiltroRol('')}>
            Todos
          </button>
          {ROLES.map((r) => (
            <button
              key={r.value}
              type="button"
              className={`chip-btn ${filtroRol === r.value ? 'active' : ''}`}
              onClick={() => setFiltroRol(r.value)}
            >
              {r.label}
            </button>
          ))}
        </div>
        <label className="check">
          <input
            type="checkbox"
            checked={mostrarInactivos}
            onChange={(e) => setMostrarInactivos(e.target.checked)}
          />
          Mostrar inactivos
        </label>
      </div>

      <div className="cards">
        {filtrados.map((u) => (
          <div key={u.id} className={`card ${u.activo ? '' : 'card-inactive'}`}>
            <div>
              <strong>{u.nombre}</strong>
              <span>
                {u.rol}
                {u.vendedor_id ? ` · ${vendedorNombre(u.vendedor_id)}` : ''}
                {' · '}
                {u.username || 'sin usuario'}
                {u.rol === 'bodega' && u.tiene_pin ? ' · PIN OK' : ''}
                {u.rol === 'bodega' && !u.tiene_pin ? ' · sin PIN' : ''}
                {!u.activo ? ' · inactivo' : ''}
              </span>
              {ROL_HINT[u.rol] ? <em className="card-app-hint">{ROL_HINT[u.rol]}</em> : null}
            </div>
            <div className="card-actions">
              {u.activo ? (
                <>
                  <button type="button" onClick={() => editar(u)}>Editar</button>
                  <button type="button" className="danger" onClick={() => desactivar(u.id)}>
                    Desactivar
                  </button>
                </>
              ) : (
                <button type="button" onClick={() => reactivar(u)}>Reactivar</button>
              )}
            </div>
          </div>
        ))}
        {!filtrados.length ? <p className="muted">No hay usuarios con este filtro.</p> : null}
      </div>
    </div>
  );
}
